import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getDecisionCriteria } from '../../api/decisions.api';
import Loading from '../../components/loading/Loading';
import { getComponentIdFromURL, transformMashupFields } from '../../utils/mashups.utils';
import GridBoard from '../../components/GridBoard/GridBoard';
import Forms from '../../factory-components/forms/Forms';
import { useEvents } from '../../hooks/useEvents';
import { IframeMapping } from '../../types/mashups';
import FlowPage from '../../components/flow/FlowPage';

const IframeComponent = ({ id, src }: any) => {
    const { subscribe } = useEvents();
    const [ dynamicSrc, setDynamicSrc ] = useState('');
    const [ dynamicVariables, setDynamicVariables ] = useState<any[]>([]);

    useEffect(() => {
        if (!dynamicSrc) {
            setDynamicSrc(src);
        }
        const variables = [ ...src.matchAll(/\{\{\w+\}\}/g) ].map(m =>
            m[0].replace(/^\{\{|\}\}$/g, ''),
        );
        const vars = variables.map(variable => (
            {
                key: variable,
                value: '',
            }
        ));

        setDynamicVariables(vars);
    }, [ src, dynamicSrc ]);

    useEffect(() => {
        return subscribe('NAVIGATE_TO_IFRAME', (payload) => {
            if (payload?.url) {
                setDynamicSrc((prev: string) => {
                    if (prev === payload.url) {
                        return prev;
                    }
                    return payload.url;
                });
            }
        });
    }, [ subscribe ]);


    useEffect(() => {
        return subscribe('REFRESH_IFRAMES', (payload) => {
            const payloadKeys = Object.keys(payload);

            const newDynamicVariables = dynamicVariables.map(variable => {
                if (!payloadKeys.includes(variable.key)) {
                    return variable;
                }

                return {
                    key: variable.key,
                    value: payload[variable.key].value,
                };
            });

            setDynamicVariables(newDynamicVariables);
        });
    }, [ subscribe, dynamicVariables ]);

    let urlIsReady = true;
    const notReadyVars: string[] = [];

    dynamicVariables.forEach(dynamicVariable => {
        if (!dynamicVariable.value) {
            urlIsReady = false;
            notReadyVars.push(dynamicVariable.key);
        }
    });

    if (dynamicVariables.length > 0 && !urlIsReady) {
        return <>Choose the {notReadyVars.join(', ')} first.</>;
    }

    let generatedUrl = dynamicSrc;

    dynamicVariables.forEach(dynamicVariable => {
        if (!dynamicVariable.value) {
            return;
        }

        generatedUrl = generatedUrl.replace(
            '{{' + dynamicVariable.key + '}}',
            dynamicVariable.value,
        );
    });

    return (
        <iframe
            id={id}
            src={generatedUrl}
            title={id}
            width="100%"
            height="100%"
            style={{
                border: 'none',
                outline: 'none',
                overflow: 'auto',
            }}
        />
    );
};

const FormComponent = ({ id, src, mappings }: any) => {
    const formId = getComponentIdFromURL(src, 'forms');
    if (!formId) {
        return <IframeComponent id={id} src={src}/>;
    }

    return (
        <div key={id} style={{ height: '100%', width: '100%', overflow: 'auto' }}>
            <Forms formId={formId} mappings={mappings}/>
        </div>
    );
};

const FlowComponent = ({ id, src }: any) => {
    const flowId = getComponentIdFromURL(src, 'flows');
    if (!flowId) {
        return <IframeComponent id={id} src={src}/>;
    }

    return (
        <div key={id} style={{ height: '100%', width: '100%', overflow: 'auto' }}>
            <FlowPage flowId={flowId}/>
        </div>
    );
};

const renderMashupComponent = (item: any, mappings: IframeMapping[]) => {
    let Component = IframeComponent;
    if (item.type === 'form') {
        Component = FormComponent;
    } else if (item.type === 'flow') {
        Component = FlowComponent;
    }

    return {
        id: item.id,
        content: <Component key={item.id} id={item.id} src={item.src} mappings={mappings}/>,
        layout: item.layout,
    };
};

const Mashups = () => {
    const [ loading, setLoading ] = useState(true);
    const [ mashupComponents, setMashupComponents ] = useState<any[]>([]);
    const [ mappings, setMapping ] = useState<IframeMapping[]>([]);
    const { mashupId } = useParams();
    const { emit } = useEvents();

    useEffect(() => {
        if (!mashupId) {
            setLoading(false);
            return;
        }

        getDecisionCriteria(mashupId)
        .then(response => {
            const { elements, iframeMapping }: {
                elements: any[]; iframeMapping: IframeMapping[];
            } = response.form_config;
            const formattedElements: any[] = [];
            elements.forEach(element => {
                const groupElements = transformMashupFields(element.children);
                groupElements.forEach(groupElement => {
                    if (groupElement.src) {
                        formattedElements.push(groupElement);
                    }
                });
            });
            setMashupComponents(formattedElements);
            setMapping(iframeMapping);
        })
        .catch((err) => {
            if ([ 401, 404 ].includes(err?.response?.status)) {
                emit('http-401', { code: err?.response?.status });
                return;
            }
        })
        .finally(() => setLoading(false));
    }, [ mashupId, emit ]);

    if (!mashupId) {
        return <Loading/>;
    }

    return (
        <div>
            {loading ? <Loading/> : (
                <div style={{ width: '95%', margin: '32px auto 0' }}>
                    <GridBoard items={mashupComponents.map((item) => renderMashupComponent(item, mappings))}/>
                </div>
            )}
        </div>
    );
};

export default Mashups;